/**
 * A list page's search, filters and sort, kept in the address.
 *
 * The address is the single source of truth: a filtered, sorted view can be
 * copied to a colleague and opens the same way. Changes replace the history
 * entry rather than adding one, so Back leaves the page instead of stepping
 * through every keystroke. Parameters the list does not own (a `?user=` that
 * another page linked with, say) are left alone, including by Clear.
 */

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type SortDir = "asc" | "desc";

export interface ListSort {
  field: string;
  dir: SortDir;
}

export interface ListUrlConfig {
  /** Address parameter holding the search text. Defaults to `q`. */
  searchKey?: string;
  /** Address parameters that hold filters. */
  filterKeys: readonly string[];
  /** Fields the list can be sorted by. */
  sortFields: readonly string[];
  /** What the list shows when the address names no sort. */
  defaultSort: ListSort;
}

export interface ListUrlState {
  search: string;
  filters: Record<string, string>;
  sort: ListSort;
  /** True when a search or any filter is set (a sort alone is not a filter). */
  hasActive: boolean;
  setSearch: (value: string) => void;
  setFilter: (key: string, value: string) => void;
  setSort: (sort: ListSort) => void;
  /** Drop the search, every filter and the sort, back to the defaults. */
  clear: () => void;
}

export function useListUrlState(config: ListUrlConfig): ListUrlState {
  const { searchKey = "q", filterKeys, sortFields, defaultSort } = config;
  const [params, setParams] = useSearchParams();

  const search = params.get(searchKey) ?? "";
  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const key of filterKeys) out[key] = params.get(key) ?? "";
    return out;
  }, [params, filterKeys]);

  const rawField = params.get("sort");
  const rawDir = params.get("dir");
  const knownField = rawField !== null && sortFields.includes(rawField);
  const sort: ListSort = {
    field: knownField ? rawField : defaultSort.field,
    dir:
      rawDir === "asc" || rawDir === "desc"
        ? rawDir
        : knownField
          ? "desc"
          : defaultSort.dir,
  };

  const update = useCallback(
    (patch: Record<string, string | undefined>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const setSort = useCallback(
    (next: ListSort) => {
      const isDefault =
        next.field === defaultSort.field && next.dir === defaultSort.dir;
      update({
        sort: isDefault ? undefined : next.field,
        dir: isDefault ? undefined : next.dir,
      });
    },
    [update, defaultSort.field, defaultSort.dir]
  );

  const clear = useCallback(() => {
    const patch: Record<string, undefined> = {
      [searchKey]: undefined,
      sort: undefined,
      dir: undefined,
    };
    for (const key of filterKeys) patch[key] = undefined;
    update(patch);
  }, [update, searchKey, filterKeys]);

  return {
    search,
    filters,
    sort,
    hasActive: search !== "" || Object.values(filters).some((v) => v !== ""),
    setSearch: (value) => update({ [searchKey]: value || undefined }),
    setFilter: (key, value) => update({ [key]: value || undefined }),
    setSort,
    clear,
  };
}

/** A sorted copy of `rows`, ordered by the getter for the chosen field. */
export function sortRows<T>(
  rows: readonly T[],
  sort: ListSort,
  getters: Record<string, (row: T) => string | number | undefined>
): T[] {
  const get = getters[sort.field];
  if (!get) return [...rows];
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = get(a) ?? "";
    const bv = get(b) ?? "";
    if (av === bv) return 0;
    return (av < bv ? -1 : 1) * sign;
  });
}
